import React from 'react';
import { Link } from 'react-router-dom';

interface OptionCardProps {
  title: string;
  imageUrl: string | null;
  route: string;
  external: boolean;
  deprecated?: boolean;
  underConstruction?: boolean;
}

const OptionCard: React.FC<OptionCardProps> = ({ title, imageUrl, route, external, deprecated, underConstruction }) => {
  const handleClick = () => {
    // Don't navigate if deprecated or under construction
    if (deprecated || underConstruction) return;
    
    if (external) {
      window.open(route, '_blank');
    }
  };

  // Determine card styling based on state
  const isDisabled = deprecated || underConstruction;
  const cardClasses = `w-72 h-72 p-4 m-4 bg-black bg-opacity-50 rounded-lg shadow-md flex flex-col items-center justify-center text-center transition-all duration-200 border-2 ${
    deprecated 
      ? 'border-gray-600 opacity-60 cursor-not-allowed' 
      : underConstruction 
        ? 'border-[#fcd34d] cursor-default' 
        : 'border-light-blue-500 cursor-pointer hover:bg-opacity-75 transform hover:scale-105'
  }`;

  const renderImage = () => {
    if (underConstruction && !imageUrl) {
      // Placeholder for under construction items without image
      return (
        <div className="w-48 h-48 mb-4 rounded bg-gray-800 flex items-center justify-center border-2 border-dashed border-[#fcd34d]">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto text-[#fcd34d] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <span className="text-[#fcd34d] text-xs">Coming Soon</span>
          </div>
        </div>
      );
    }

    if (!imageUrl) return null;

    return (
      <div className="relative">
        <img
          src={imageUrl}
          alt={title}
          className={`w-48 h-48 mb-4 object-cover object-center rounded ${
            deprecated ? 'grayscale' : ''
          }`}
        />
        {deprecated && (
          <div className="absolute inset-0 flex items-center justify-center mb-4">
            <div className="bg-red-900 bg-opacity-80 text-white text-xs font-bold px-3 py-1 rounded transform -rotate-12">
              DEPRECATED
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTitle = () => (
    <div className="relative">
      <h3 className={`text-2xl font-semibold ${
        deprecated 
          ? 'text-gray-500 line-through decoration-red-500 decoration-2' 
          : underConstruction 
            ? 'text-[#fcd34d]' 
            : 'text-white'
      }`}>
        {title}
      </h3>
      {underConstruction && (
        <span className="block text-xs text-[#fcd34d] mt-1 animate-pulse">
          🚧 Under Construction
        </span>
      )}
    </div>
  );

  // For disabled items, don't wrap in Link
  if (isDisabled) {
    return (
      <div className={cardClasses} onClick={handleClick}>
        {renderImage()}
        {renderTitle()}
      </div>
    );
  }

  return (
    <div className={cardClasses} onClick={handleClick}>
      {external ? (
        <>
          {renderImage()}
          {renderTitle()}
        </>
      ) : (
        <Link to={route}>
          {renderImage()}
          {renderTitle()}
        </Link>
      )}
    </div>
  );
};

export default OptionCard;
